// src/components/ClientManagement/ClientView.jsx
import React, { useState, useEffect, useRef } from 'react';
import { db } from '../../firebase/config';
import {
  collection, getDocs, addDoc, updateDoc, deleteDoc,
  doc, arrayUnion, arrayRemove, getDoc
} from 'firebase/firestore';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const ClientView = ({ currentUser, userRole }) => {
  const [clientsData, setClientsData] = useState([]);
  const [users, setUsers] = useState([]);
  const [carpentersList, setCarpentersList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  // ─── EDIT CLIENT STATE ──────────────────────────────────────────────────
  const [editingClient, setEditingClient] = useState(null); // { rowId, currentName }
  const [editClientValue, setEditClientValue] = useState('');

  // Modals
  const [showAddClientModal, setShowAddClientModal] = useState(false);
  const [newClientName, setNewClientName] = useState('');
  const [clientSearchTerm, setClientSearchTerm] = useState('');
  const [filteredClients, setFilteredClients] = useState([]);

  // Add / Edit items
  const [addingItem, setAddingItem] = useState(null);
  const [newItemText, setNewItemText] = useState('');
  const [newItemCarpenterId, setNewItemCarpenterId] = useState('');
  const [editingItem, setEditingItem] = useState(null);
  const [editItemValue, setEditItemValue] = useState('');

  // Carpenter modal (kept but not used for display)
  const [showCarpenterSearchModal, setShowCarpenterSearchModal] = useState(false);
  const [currentCarpenterRowId, setCurrentCarpenterRowId] = useState(null);
  const [carpenterSearchTerm, setCarpenterSearchTerm] = useState('');
  const [filteredCarpenterEmployees, setFilteredCarpenterEmployees] = useState([]);
  const [newCarpenterName, setNewCarpenterName] = useState('');

  // Drag state
  const [itemDragOver, setItemDragOver] = useState({ rowId: null, index: null });
  const [carpenterDragOver, setCarpenterDragOver] = useState({ rowId: null, index: null });
  const [rowDragOver, setRowDragOver] = useState(null);

  const isProcessingItemDrop = useRef(false);
  const isProcessingCarpenterDrop = useRef(false);

  const columnColors = {
    rowNumber:  { bg: '#F8FAFC', border: '#E2E8F0' },
    client:     { bg: '#FFF5F5', border: '#FED7D7' },
    items:      { bg: '#FFFAF0', border: '#FDE68A' },
    carpenters: { bg: '#EFF6FF', border: '#BFDBFE' },
  };

  // ─── PDF export ───────────────────────────────────────────────────────────
  const exportToPDF = () => {
    const d = new jsPDF({ orientation: 'landscape', unit: 'mm' });
    d.setFontSize(18); d.setTextColor(128, 0, 32);
    d.text('Client View Report', 14, 20);
    d.setFontSize(10); d.setTextColor(100, 100, 100);
    d.text(`Generated: ${new Date().toLocaleString()}`, 14, 30);
    const fmt = (arr) => {
      if (!arr || !arr.length) return '—';
      return arr.map(item => typeof item === 'object' ? item.text : item).join('\n');
    };
    autoTable(d, {
      head: [['#', 'Client', 'Items', 'Carpenters']],
      body: clientsData.map((row, i) => [
        String(i + 1), row.client || '—', fmt(row.items), fmt(row.carpenters),
      ]),
      startY: 40,
      theme: 'plain',
      headStyles: { fillColor: [128, 0, 32], textColor: [255, 255, 255], fontSize: 11, fontStyle: 'bold', halign: 'left', valign: 'middle', lineWidth: 0.1, lineColor: [255, 255, 255] },
      bodyStyles: { fontSize: 9, cellPadding: 2, valign: 'top', lineWidth: 0.1, lineColor: [226, 232, 240] },
      columnStyles: { 0: { fillColor: [248, 250, 252] }, 1: { fillColor: [255, 245, 245] }, 2: { fillColor: [255, 250, 240] }, 3: { fillColor: [239, 246, 255] } },
    });
    d.save(`client-view-${new Date().toISOString().split('T')[0]}.pdf`);
  };

  // ─── Fetch ────────────────────────────────────────────────────────────────
  useEffect(() => { fetchUsers(); }, []);
  useEffect(() => { fetchCarpenters(); }, []);
  useEffect(() => { fetchClients(); }, []);

  useEffect(() => {
    if (!clientSearchTerm.trim()) { setFilteredClients([]); return; }
    const t = clientSearchTerm.toLowerCase();
    setFilteredClients(clientsData.filter(c => c.client?.toLowerCase().includes(t)).slice(0, 10));
  }, [clientSearchTerm, clientsData]);

  useEffect(() => {
    if (!carpenterSearchTerm.trim()) { setFilteredCarpenterEmployees([]); return; }
    const t = carpenterSearchTerm.toLowerCase();
    setFilteredCarpenterEmployees(
      users.filter(u => u.name?.toLowerCase().includes(t) || u.role?.toLowerCase().includes(t) || u.email?.toLowerCase().includes(t)).slice(0, 10)
    );
  }, [carpenterSearchTerm, users]);

  const fetchUsers = async () => {
    try {
      const snap = await getDocs(collection(db, 'users'));
      setUsers(snap.docs.map(d => ({ id: d.id, name: d.data().name || 'Unnamed', role: d.data().role || 'worker', email: d.data().email || '' })));
    } catch (e) { console.error('fetchUsers:', e); }
  };

  const fetchCarpenters = async () => {
    try {
      const snap = await getDocs(collection(db, 'projectPlanner'));
      setCarpentersList(snap.docs.map(d => ({ id: d.id, carpenter: d.data().carpenter || 'Unnamed' })));
    } catch (e) { console.error('fetchCarpenters:', e); }
  };

  const fetchClients = async () => {
    setLoading(true);
    try {
      const snap = await getDocs(collection(db, 'clients'));
      if (snap.empty) { setClientsData([]); return; }
      const ensure = (f) => {
        if (!f) return [];
        if (Array.isArray(f)) return f;
        if (typeof f === 'string') return f ? [f] : [];
        return [];
      };
      let data = snap.docs.map(d => ({
        id: d.id,
        client:     d.data().name || d.data().client || '',
        items:      ensure(d.data().items),
        carpenters: ensure(d.data().carpenters),
        order:      d.data().order !== undefined ? d.data().order : null,
      }));
      const hasOrder = data.some(r => r.order !== null);
      if (!hasOrder) {
        data = data.map((r, i) => ({ ...r, order: i }));
        for (const r of data) await updateDoc(doc(db, 'clients', r.id), { order: r.order });
      }
      data.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
      setClientsData(data);
    } catch (e) { console.error('fetchClients:', e); }
    finally { setLoading(false); }
  };

  // ─── CLIENT EDIT FUNCTIONS ──────────────────────────────────────────────
  const startEditClient = (rowId, currentName) => {
    setEditingClient({ rowId, currentName });
    setEditClientValue(currentName);
  };

  const cancelEditClient = () => {
    setEditingClient(null);
    setEditClientValue('');
  };

  const saveEditClient = async (rowId) => {
    if (!editClientValue.trim()) { alert('Client name cannot be empty'); return; }
    const newName = editClientValue.trim();
    // Check for duplicate names (case-insensitive)
    if (clientsData.some(r => r.id !== rowId && r.client.toLowerCase() === newName.toLowerCase())) {
      alert(`A client with the name "${newName}" already exists.`);
      return;
    }
    try {
      const clientRef = doc(db, 'clients', rowId);
      await updateDoc(clientRef, { name: newName });
      // Also update local state
      setClientsData(prev => prev.map(r =>
        r.id === rowId ? { ...r, client: newName } : r
      ));
      cancelEditClient();
    } catch (e) {
      console.error('Error updating client name:', e);
      alert('Failed to update client name.');
    }
  };

  // ─── SYNC HELPERS ──────────────────────────────────────────────────────────
  // Find item by text and clientId only (ignores carpenterId)
  const findPlannerItem = (plannerData, text, clientId) => {
    const columns = ['currentWork', 'nextPlanned', 'preparation', 'pipeLine'];
    for (const col of columns) {
      const arr = plannerData[col] || [];
      for (const item of arr) {
        if (typeof item === 'object' && item.text === text && item.clientId === clientId) {
          return { column: col, item };
        }
      }
    }
    return null;
  };

  const syncItemToPlanner = async (itemText, carpenterId, clientId, carpenterName, operation) => {
    if (!carpenterId) return;
    const plannerRef = doc(db, 'projectPlanner', carpenterId);
    const taskObj = { text: itemText, clientId, carpenterId };
    if (operation === 'add') {
      await updateDoc(plannerRef, { preparation: arrayUnion(taskObj) });
      if (clientId && carpenterName) {
        const clientRef = doc(db, 'clients', clientId);
        await updateDoc(clientRef, { carpenters: arrayUnion(carpenterName) });
      }
    } else if (operation === 'remove') {
      await updateDoc(plannerRef, { preparation: arrayRemove(taskObj) });
    }
  };

  // ─── Client CRUD ──────────────────────────────────────────────────────────
  const handleAddClient = async (selectedClient = null) => {
    const name = (selectedClient ? selectedClient.client : newClientName).trim();
    if (!name) { alert('Please enter a client name'); return; }
    if (clientsData.some(r => r.client.toLowerCase() === name.toLowerCase())) { alert(`"${name}" already exists.`); return; }
    try {
      const maxOrder = clientsData.reduce((m, r) => Math.max(m, r.order ?? 0), 0);
      const newRow = { name, items: [], carpenters: [], order: maxOrder + 1 };
      const ref = await addDoc(collection(db, 'clients'), newRow);
      setClientsData(prev => [...prev, { id: ref.id, client: name, items: [], carpenters: [], order: maxOrder + 1 }]);
      setNewClientName(''); setClientSearchTerm(''); setShowAddClientModal(false);
    } catch (e) { console.error('addClient:', e); alert('Failed to add client'); }
  };

  const handleDeleteClient = async (rowId, clientName) => {
    if (!window.confirm(`Delete "${clientName}"? This cannot be undone.`)) return;
    try {
      await deleteDoc(doc(db, 'clients', rowId));
      setClientsData(prev => prev.filter(r => r.id !== rowId));
    } catch (e) { console.error('deleteClient:', e); alert('Failed to delete client'); }
  };

  // ─── Items CRUD ──────────────────────────────────────────────────────────
  const handleAddItem = async (rowId) => {
    if (!newItemText.trim()) { alert('Please enter an item'); return; }
    const carpenterName = newItemCarpenterId 
      ? carpentersList.find(c => c.id === newItemCarpenterId)?.carpenter || null
      : null;
    const itemObj = { text: newItemText.trim(), carpenterId: newItemCarpenterId || null };
    try {
      await updateDoc(doc(db, 'clients', rowId), { items: arrayUnion(itemObj) });
      setClientsData(prev => prev.map(r => r.id === rowId ? { ...r, items: [...(r.items || []), itemObj] } : r));
      if (newItemCarpenterId && carpenterName) {
        await updateDoc(doc(db, 'clients', rowId), { carpenters: arrayUnion(carpenterName) });
      }
      if (newItemCarpenterId) {
        await syncItemToPlanner(newItemText.trim(), newItemCarpenterId, rowId, carpenterName, 'add');
      }
      setNewItemText('');
      setNewItemCarpenterId('');
      setAddingItem(null);
    } catch (e) { console.error('addItem:', e); alert('Failed to add item'); }
  };

  const handleRemoveItem = async (rowId, itemToRemove) => {
    const text = typeof itemToRemove === 'object' ? itemToRemove.text : itemToRemove;
    if (!window.confirm(`Remove "${text}"?`)) return;
    try {
      await updateDoc(doc(db, 'clients', rowId), { items: arrayRemove(itemToRemove) });
      setClientsData(prev => prev.map(r => r.id === rowId ? { ...r, items: (r.items || []).filter(i => i !== itemToRemove) } : r));

      if (typeof itemToRemove === 'object' && itemToRemove.carpenterId) {
        const plannerRef = doc(db, 'projectPlanner', itemToRemove.carpenterId);
        const plannerSnap = await getDoc(plannerRef);
        if (plannerSnap.exists()) {
          const plannerData = plannerSnap.data();
          const result = findPlannerItem(plannerData, text, rowId);
          if (result) {
            await updateDoc(plannerRef, {
              [result.column]: arrayRemove(result.item)
            });
          } else {
            const fallbackObj = { text, clientId: rowId, carpenterId: itemToRemove.carpenterId };
            await updateDoc(plannerRef, { preparation: arrayRemove(fallbackObj) });
          }
        }
      }
    } catch (e) { console.error('removeItem:', e); alert('Failed to remove item'); }
  };

  // ─── Edit functions for items ────────────────────────────────────────────
  const startEditItem = (rowId, index, item) => {
    setEditingItem({ rowId, index });
    const text = typeof item === 'object' ? item.text : item;
    setEditItemValue(text);
  };

  const cancelEditItem = () => { setEditingItem(null); setEditItemValue(''); };

  const saveEditItem = async (rowId, index, newValue) => {
    if (!newValue.trim()) { alert('Item cannot be empty'); return; }
    const row = clientsData.find(r => r.id === rowId);
    if (!row) return;
    const oldArray = [...(row.items || [])];
    const oldItem = oldArray[index];
    const oldText = typeof oldItem === 'object' ? oldItem.text : oldItem;
    if (oldText === newValue.trim()) { cancelEditItem(); return; }

    const newItem = typeof oldItem === 'object'
      ? { ...oldItem, text: newValue.trim() }
      : newValue.trim();
    oldArray[index] = newItem;

    try {
      await updateDoc(doc(db, 'clients', rowId), { items: oldArray });
      setClientsData(prev => prev.map(r => r.id === rowId ? { ...r, items: oldArray } : r));

      if (typeof oldItem === 'object' && oldItem.carpenterId) {
        const plannerRef = doc(db, 'projectPlanner', oldItem.carpenterId);
        const plannerSnap = await getDoc(plannerRef);
        if (plannerSnap.exists()) {
          const plannerData = plannerSnap.data();
          const result = findPlannerItem(plannerData, oldText, rowId);
          if (result) {
            const newObj = { ...result.item, text: newValue.trim() };
            await updateDoc(plannerRef, {
              [result.column]: arrayRemove(result.item)
            });
            await updateDoc(plannerRef, {
              [result.column]: arrayUnion(newObj)
            });
          } else {
            console.warn('Item not found in planner, will not update.');
          }
        }
      }
      cancelEditItem();
    } catch (e) {
      console.error('saveEditItem:', e);
      alert('Failed to edit item');
      await fetchClients();
    }
  };

  // ─── Carpenters CRUD (kept but no longer used for display) ──────────────
  const handleAddCarpenter = async (rowId, selectedEmployee = null) => { /* no-op */ };
  const handleRemoveCarpenter = async (rowId, carpenterToRemove) => { /* no-op */ };

  // ═══════════════════════════════════════════════════════════════════════════
  // DRAG & DROP — ITEMS (with cross-sync)
  // ═══════════════════════════════════════════════════════════════════════════

  const handleItemReorder = async (rowId, fromIndex, toIndex) => {
    if (fromIndex === toIndex) return;
    const row = clientsData.find(r => r.id === rowId);
    if (!row) return;
    const arr = [...(row.items || [])];
    const [moved] = arr.splice(fromIndex, 1);
    arr.splice(toIndex, 0, moved);
    setClientsData(prev => prev.map(r => r.id === rowId ? { ...r, items: arr } : r));
    try {
      await updateDoc(doc(db, 'clients', rowId), { items: arr });
    } catch (e) {
      console.error('itemReorder Firestore:', e);
      await fetchClients();
    }
  };

  const moveItemBetweenRows = async (sourceRowId, itemValue, targetRowId) => {
    if (isProcessingItemDrop.current) return;
    if (sourceRowId === targetRowId) return;
    isProcessingItemDrop.current = true;

    const sourceRow = clientsData.find(r => r.id === sourceRowId);
    const targetRow = clientsData.find(r => r.id === targetRowId);
    if (!sourceRow || !targetRow) return;
    const itemToMove = (sourceRow.items || []).find(i => (typeof i === 'object' ? i.text : i) === itemValue);
    if (!itemToMove) return;

    setClientsData(prev => prev.map(row => {
      if (row.id === sourceRowId) return { ...row, items: (row.items || []).filter(i => i !== itemToMove) };
      if (row.id === targetRowId) return { ...row, items: [...(row.items || []), itemToMove] };
      return row;
    }));

    try {
      await updateDoc(doc(db, 'clients', sourceRowId), { items: arrayRemove(itemToMove) });
      await updateDoc(doc(db, 'clients', targetRowId), { items: arrayUnion(itemToMove) });

      if (typeof itemToMove === 'object' && itemToMove.carpenterId) {
        const plannerRef = doc(db, 'projectPlanner', itemToMove.carpenterId);
        const plannerSnap = await getDoc(plannerRef);
        if (plannerSnap.exists()) {
          const plannerData = plannerSnap.data();
          const result = findPlannerItem(plannerData, itemToMove.text, sourceRowId);
          if (result) {
            const newObj = { ...result.item, clientId: targetRowId };
            await updateDoc(plannerRef, {
              [result.column]: arrayRemove(result.item)
            });
            await updateDoc(plannerRef, {
              [result.column]: arrayUnion(newObj)
            });
          } else {
            const oldObj = { text: itemToMove.text, clientId: sourceRowId, carpenterId: itemToMove.carpenterId };
            const newObj = { text: itemToMove.text, clientId: targetRowId, carpenterId: itemToMove.carpenterId };
            await updateDoc(plannerRef, { preparation: arrayRemove(oldObj) });
            await updateDoc(plannerRef, { preparation: arrayUnion(newObj) });
          }
        }

        const targetCarpenterName = carpentersList.find(c => c.id === itemToMove.carpenterId)?.carpenter;
        if (targetCarpenterName) {
          await updateDoc(doc(db, 'clients', targetRowId), { carpenters: arrayUnion(targetCarpenterName) });
        }
      }
    } catch (err) {
      console.error('moveItem Firestore:', err);
      await fetchClients();
      alert('Failed to move item. Changes reverted.');
    } finally {
      isProcessingItemDrop.current = false;
    }
  };

  const onItemDragStart = (e, rowId, index) => {
    e.dataTransfer.setData('text/plain', JSON.stringify({ type: 'item', rowId, index }));
    e.dataTransfer.effectAllowed = 'move';
  };

  const onItemDrop = async (e, targetRowId, targetIndex) => {
    e.preventDefault();
    e.stopPropagation();
    setItemDragOver({ rowId: null, index: null });

    const raw = e.dataTransfer.getData('text/plain');
    if (!raw) return;
    let data; try { data = JSON.parse(raw); } catch { return; }
    if (data.type !== 'item') return;

    if (data.rowId === targetRowId) {
      await handleItemReorder(targetRowId, data.index, targetIndex);
    } else {
      const srcRow = clientsData.find(r => r.id === data.rowId);
      if (!srcRow) return;
      const item = srcRow.items?.[data.index];
      if (item == null) return;
      const itemText = typeof item === 'object' ? item.text : item;
      await moveItemBetweenRows(data.rowId, itemText, targetRowId);
    }
  };

  const onItemsColumnDrop = async (e, targetRowId) => {
    e.preventDefault();
    e.stopPropagation();
    setItemDragOver({ rowId: null, index: null });

    const raw = e.dataTransfer.getData('text/plain');
    if (!raw) return;
    let data; try { data = JSON.parse(raw); } catch { return; }
    if (data.type !== 'item') return;
    if (data.rowId === targetRowId) return;

    const srcRow = clientsData.find(r => r.id === data.rowId);
    if (!srcRow) return;
    const item = srcRow.items?.[data.index];
    if (item == null) return;
    const itemText = typeof item === 'object' ? item.text : item;
    await moveItemBetweenRows(data.rowId, itemText, targetRowId);
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // DRAG & DROP — CARPENTERS (no longer interactive)
  // ═══════════════════════════════════════════════════════════════════════════
  const handleCarpenterReorder = async (rowId, fromIndex, toIndex) => { /* no-op */ };
  const moveCarpenterBetweenRows = async (sourceRowId, carpenterValue, targetRowId) => { /* no-op */ };
  const onCarpenterDragStart = (e, rowId, index) => { /* no-op */ };
  const onCarpenterDrop = async (e, targetRowId, targetIndex) => { e.preventDefault(); e.stopPropagation(); };
  const onCarpenterColumnDrop = async (e, targetRowId) => { e.preventDefault(); };

  // ═══════════════════════════════════════════════════════════════════════════
  // DRAG & DROP — ROW REORDER
  // ═══════════════════════════════════════════════════════════════════════════
  const onRowDragStart = (e, rowId) => {
    e.dataTransfer.setData('text/plain', JSON.stringify({ type: 'row', rowId }));
    e.dataTransfer.effectAllowed = 'move';
  };

  const onRowDrop = async (e, targetRowId) => {
    e.preventDefault();
    setRowDragOver(null);

    const raw = e.dataTransfer.getData('text/plain');
    if (!raw) return;
    let data; try { data = JSON.parse(raw); } catch { return; }
    if (data.type !== 'row' || data.rowId === targetRowId) return;

    const src = clientsData.find(r => r.id === data.rowId);
    const tgt = clientsData.find(r => r.id === targetRowId);
    if (!src || !tgt) return;
    try {
      await updateDoc(doc(db, 'clients', src.id), { order: tgt.order });
      await updateDoc(doc(db, 'clients', tgt.id), { order: src.order });
      setClientsData(prev =>
        prev.map(row => {
          if (row.id === src.id) return { ...row, order: tgt.order };
          if (row.id === tgt.id) return { ...row, order: src.order };
          return row;
        }).sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
      );
    } catch (e) { console.error('rowReorder:', e); alert('Failed to reorder rows'); }
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // RENDER HELPERS
  // ═══════════════════════════════════════════════════════════════════════════

  const renderItemCell = (rowId, items) => {
    const list = Array.isArray(items) ? items : [];
    return (
      <div style={{ width: '100%' }}>
        {list.length > 0 ? list.map((item, idx) => {
          const itemText = typeof item === 'object' ? item.text : item;
          const carpenterId = typeof item === 'object' ? item.carpenterId : null;
          const carpenter = carpenterId ? carpentersList.find(c => c.id === carpenterId) : null;
          const carpenterName = carpenter ? carpenter.carpenter : null;

          const isEditing = editingItem?.rowId === rowId && editingItem?.index === idx;
          const isHovered = itemDragOver.rowId === rowId && itemDragOver.index === idx;
          return (
            <div
              key={`${itemText}-${idx}`}
              draggable={!isEditing}
              onDragStart={(e) => { if (!isEditing) onItemDragStart(e, rowId, idx); }}
              onDragOver={(e) => {
                if (!isEditing) {
                  e.preventDefault();
                  e.stopPropagation();
                  setItemDragOver({ rowId, index: idx });
                }
              }}
              onDragLeave={(e) => {
                e.stopPropagation();
                if (!e.currentTarget.contains(e.relatedTarget)) {
                  setItemDragOver(prev => prev.rowId === rowId && prev.index === idx ? { rowId: null, index: null } : prev);
                }
              }}
              onDrop={(e) => onItemDrop(e, rowId, idx)}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                background: isEditing ? '#f0f9ff' : isHovered ? '#dbeafe' : '#ffffff',
                padding: '6px 10px', marginBottom: '6px', borderRadius: '6px',
                fontSize: '12px', border: `1px solid ${isHovered ? '#93c5fd' : columnColors.items.border}`,
                boxShadow: '0 1px 2px rgba(0,0,0,0.02)',
                cursor: isEditing ? 'default' : 'grab',
                transition: 'background 0.12s, border-color 0.12s',
                wordBreak: 'break-word',
              }}
            >
              {isEditing ? (
                <div style={{ display: 'flex', gap: '6px', flex: 1 }}>
                  <input type="text" value={editItemValue} onChange={e => setEditItemValue(e.target.value)}
                    onKeyPress={e => { if (e.key === 'Enter') saveEditItem(rowId, idx, editItemValue); }}
                    autoFocus
                    style={{ flex: 1, padding: '4px 6px', borderRadius: '4px', border: '1px solid #2563eb', outline: 'none', fontSize: '12px' }}
                  />
                  <button onClick={() => saveEditItem(rowId, idx, editItemValue)} style={{ background: '#10b981', color: 'white', border: 'none', padding: '2px 8px', borderRadius: '4px', cursor: 'pointer', fontSize: '11px' }}>Save</button>
                  <button onClick={cancelEditItem} style={{ background: '#9ca3af', color: 'white', border: 'none', padding: '2px 8px', borderRadius: '4px', cursor: 'pointer', fontSize: '11px' }}>Cancel</button>
                </div>
              ) : (
                <>
                  <span style={{ flex: 1 }}>
                    ⠿ {itemText}
                    {carpenterName && (
                      <span style={{ 
                        color: '#64748b', 
                        marginLeft: '8px', 
                        fontSize: '10px', 
                        fontStyle: 'italic',
                        borderLeft: '1px solid #e2e8f0',
                        paddingLeft: '8px'
                      }}>
                        {carpenterName}
                      </span>
                    )}
                  </span>
                  <div style={{ display: 'flex', gap: '4px' }}>
                    <button onClick={() => startEditItem(rowId, idx, item)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '12px', color: '#2563eb', padding: '2px 6px', borderRadius: '4px' }} title="Edit">✏️</button>
                    <button onClick={() => handleRemoveItem(rowId, item)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '14px', color: '#dc2626', padding: '2px 6px', borderRadius: '4px' }} title="Remove">✕</button>
                  </div>
                </>
              )}
            </div>
          );
        }) : (
          <div style={{ color: '#94a3b8', fontSize: '12px', fontStyle: 'italic', padding: '8px', textAlign: 'center', background: '#f8fafc', borderRadius: '6px', marginBottom: '6px' }}>
            No items — drag one here or add below
          </div>
        )}

        {addingItem?.rowId === rowId && addingItem?.field === 'items' ? (
          <div style={{ display: 'flex', gap: '6px', marginTop: '8px', flexWrap: 'wrap' }}>
            <input type="text" value={newItemText} onChange={e => setNewItemText(e.target.value)}
              onKeyPress={e => e.key === 'Enter' && handleAddItem(rowId)}
              placeholder="Enter item..." autoFocus
              style={{ flex: '2 1 150px', padding: '8px 10px', borderRadius: '8px', border: '1px solid #800020', outline: 'none', fontSize: '12px' }}
            />
            <select
              value={newItemCarpenterId}
              onChange={(e) => setNewItemCarpenterId(e.target.value)}
              style={{ flex: '1 1 120px', padding: '8px 10px', borderRadius: '8px', border: '1px solid #e2e8f0', outline: 'none', fontSize: '12px', background: 'white' }}
            >
              <option value="">No Carpenter</option>
              {carpentersList.map(c => <option key={c.id} value={c.id}>{c.carpenter}</option>)}
            </select>
            <button onClick={() => handleAddItem(rowId)} style={{ background: '#10b981', color: 'white', border: 'none', padding: '6px 12px', borderRadius: '6px', cursor: 'pointer', fontSize: '11px', fontWeight: '500' }}>Add</button>
            <button onClick={() => { setAddingItem(null); setNewItemText(''); setNewItemCarpenterId(''); }} style={{ background: '#9ca3af', color: 'white', border: 'none', padding: '6px 12px', borderRadius: '6px', cursor: 'pointer', fontSize: '11px', fontWeight: '500' }}>Cancel</button>
          </div>
        ) : (
          <button
            onClick={() => setAddingItem({ rowId, field: 'items' })}
            style={{ background: 'none', border: '1px dashed #cbd5e1', padding: '6px 10px', borderRadius: '8px', cursor: 'pointer', fontSize: '11px', color: '#64748b', width: '100%', marginTop: '6px', transition: 'all 0.2s ease' }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = '#800020'; e.currentTarget.style.color = '#800020'; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = '#cbd5e1'; e.currentTarget.style.color = '#64748b'; }}
          >+ Add Item</button>
        )}
      </div>
    );
  };

  // ─── Carpenters column mirrors items order ──────────────────────────────
  const renderCarpenterCell = (rowId, carpenters) => {
    const row = clientsData.find(r => r.id === rowId);
    if (!row) return <div>No items</div>;
    const items = row.items || [];

    return (
      <div style={{ width: '100%' }}>
        {items.length > 0 ? items.map((item, idx) => {
          const carpenterId = typeof item === 'object' ? item.carpenterId : null;
          const carpenter = carpenterId ? carpentersList.find(c => c.id === carpenterId) : null;
          const carpenterName = carpenter ? carpenter.carpenter : '—';
          return (
            <div
              key={`${carpenterName}-${idx}`}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                background: '#ffffff',
                padding: '6px 10px', marginBottom: '6px', borderRadius: '6px',
                fontSize: '12px', border: `1px solid ${columnColors.carpenters.border}`,
                boxShadow: '0 1px 2px rgba(0,0,0,0.02)',
                wordBreak: 'break-word',
              }}
            >
              <span style={{ flex: 1 }}>⠿ {carpenterName}</span>
            </div>
          );
        }) : (
          <div style={{ color: '#94a3b8', fontSize: '12px', fontStyle: 'italic', padding: '8px', textAlign: 'center', background: '#f8fafc', borderRadius: '6px', marginBottom: '6px' }}>
            No carpenters assigned
          </div>
        )}
      </div>
    );
  };

  // ─── Main render ──────────────────────────────────────────────────────────
  const filteredData = clientsData.filter(row => row.client?.toLowerCase().includes(searchTerm.toLowerCase()));
  const canEdit = ['manager', 'ceo', 'managing-director', 'quality-control'].includes(userRole);

  if (loading) return (
    <div className="loading-state">
      <div className="loading-spinner"/>
      <p>Loading clients...</p>
    </div>
  );

  return (
    <div style={{ padding: '24px', background: '#f8fafc', minHeight: '100vh' }}>

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '16px' }}>
        <div>
          <h1 style={{ fontSize: '28px', fontWeight: '700', margin: 0, color: '#1e293b' }}>👥 Client View</h1>
          <p style={{ color: '#64748b', marginTop: '4px' }}>Manage clients, their items, and assigned carpenters</p>
        </div>
        <div style={{ display: 'flex', gap: '12px' }}>
          <input type="text" placeholder="🔍 Search client..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
            style={{ padding: '10px 16px', borderRadius: '12px', border: '1px solid #e2e8f0', width: '250px', outline: 'none', fontSize: '14px' }} />
          <button onClick={exportToPDF} style={{ background: '#800020', color: 'white', border: 'none', padding: '10px 20px', borderRadius: '12px', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}>📄 Export PDF</button>
          {canEdit && (
            <button onClick={() => setShowAddClientModal(true)} style={{ background: 'linear-gradient(135deg, #800020, #5a0016)', color: 'white', border: 'none', padding: '10px 20px', borderRadius: '12px', fontWeight: '600', cursor: 'pointer' }}>+ Add Client</button>
          )}
        </div>
      </div>

      {/* Add Client Modal */}
      {showAddClientModal && (
        <div className="modal-overlay" onClick={() => setShowAddClientModal(false)}>
          <div className="modal-content" style={{ maxWidth: '500px', borderRadius: '20px' }} onClick={e => e.stopPropagation()}>
            <h3 style={{ color: '#800020' }}>➕ Add Client</h3>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#334155' }}>Search Existing Client</label>
            <div style={{ position: 'relative' }}>
              <input type="text" placeholder="🔍 Type to search existing clients..." value={clientSearchTerm} onChange={e => setClientSearchTerm(e.target.value)}
                style={{ width: '100%', padding: '12px', borderRadius: '10px', border: '1px solid #e2e8f0', fontSize: '14px', marginBottom: '12px', outline: 'none' }} />
              {clientSearchTerm && filteredClients.length > 0 && (
                <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: 'white', border: '1px solid #e2e8f0', borderRadius: '10px', maxHeight: '200px', overflowY: 'auto', zIndex: 1000, boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
                  {filteredClients.map(client => (
                    <div key={client.id} onClick={() => { handleAddClient(client); setClientSearchTerm(''); }}
                      style={{ padding: '12px', cursor: 'pointer', borderBottom: '1px solid #f0f0f0' }}
                      onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                      onMouseLeave={e => e.currentTarget.style.background = 'white'}>
                      <div style={{ fontWeight: '500', color: '#1e293b' }}>{client.client}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div style={{ textAlign: 'center', margin: '12px 0', color: '#94a3b8', fontSize: '12px' }}>— OR enter a new name —</div>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#334155' }}>New Client Name</label>
            <input type="text" value={newClientName} onChange={e => setNewClientName(e.target.value)}
              placeholder="Enter a new client name" onKeyPress={e => e.key === 'Enter' && handleAddClient()}
              style={{ width: '100%', padding: '12px', borderRadius: '10px', border: '1px solid #e2e8f0', marginBottom: '20px', fontSize: '14px', outline: 'none' }} />
            <div style={{ display: 'flex', gap: '12px' }}>
              <button onClick={() => handleAddClient()} style={{ flex: 1, background: '#800020', color: 'white', border: 'none', padding: '12px', borderRadius: '10px', fontWeight: '600', cursor: 'pointer' }}>Add Client</button>
              <button onClick={() => { setShowAddClientModal(false); setClientSearchTerm(''); setNewClientName(''); }} style={{ flex: 1, background: '#f1f5f9', color: '#475569', border: 'none', padding: '12px', borderRadius: '10px', fontWeight: '600', cursor: 'pointer' }}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* The "Add Carpenter" modal is removed because carpenters are assigned via items */}

      {/* Empty State */}
      {clientsData.length === 0 && (
        <div style={{ textAlign: 'center', padding: '80px', background: 'white', borderRadius: '20px', border: '1px solid #e2e8f0' }}>
          <div style={{ fontSize: '64px', marginBottom: '16px' }}>👥</div>
          <h3 style={{ marginBottom: '8px', color: '#1e293b' }}>No Clients Yet</h3>
          <p style={{ color: '#64748b', marginBottom: '24px' }}>Click "Add Client" to start managing clients</p>
          {canEdit && <button onClick={() => setShowAddClientModal(true)} style={{ background: '#800020', color: 'white', border: 'none', padding: '12px 24px', borderRadius: '12px', fontWeight: '600', cursor: 'pointer' }}>+ Add First Client</button>}
        </div>
      )}

      {/* Data Table */}
      {clientsData.length > 0 && (
        <div style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: 'calc(100vh - 200px)', background: 'white', borderRadius: '20px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', border: '1px solid #e2e8f0' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '750px', tableLayout: 'fixed' }}>
            <thead style={{ position: 'sticky', top: 0, zIndex: 10 }}>
              <tr style={{ background: '#800020' }}>
                {[
                  { label: '##',            w: '50px',  align: 'center' },
                  { label: '👤 CLIENTS',    w: '22%',   align: 'left'   },
                  { label: '📦 ITEMS',      w: '35%',   align: 'left'   },
                  { label: '👥 CARPENTERS', w: '33%',   align: 'left'   },
                ].map((col, i) => (
                  <th key={i} style={{ padding: '16px', textAlign: col.align, fontWeight: '600', fontSize: '13px', textTransform: 'uppercase', letterSpacing: '0.5px', color: 'white', borderRight: '1px solid rgba(255,255,255,0.2)', width: col.w, background: '#800020', position: 'sticky', top: 0, zIndex: 11 }}>
                    {col.label}
                  </th>
                ))}
                {canEdit && <th style={{ padding: '16px', textAlign: 'center', fontWeight: '600', fontSize: '13px', textTransform: 'uppercase', letterSpacing: '0.5px', color: 'white', width: '80px', background: '#800020', position: 'sticky', top: 0, zIndex: 11 }}>ACTIONS</th>}
              </tr>
            </thead>
            <tbody>
              {filteredData.map((row, index) => (
                <tr
                  key={row.id}
                  style={{ background: rowDragOver === row.id ? '#f0f4ff' : 'transparent', transition: 'background 0.15s' }}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => onRowDrop(e, row.id)}
                  onDragEnter={(e) => setRowDragOver(row.id)}
                  onDragLeave={(e) => {
                    if (!e.currentTarget.contains(e.relatedTarget)) setRowDragOver(null);
                  }}
                >
                  {/* Row number */}
                  <td style={{ padding: '14px 16px', borderBottom: '1px solid #e2e8f0', borderRight: '1px solid #e2e8f0', background: columnColors.rowNumber.bg, textAlign: 'center', fontWeight: '500', color: '#475569' }}>
                    {index + 1}
                  </td>

                  {/* Client name — now editable */}
                  <td style={{ padding: '14px 16px', borderBottom: '1px solid #e2e8f0', borderRight: '1px solid #e2e8f0', background: columnColors.client.bg, fontWeight: '500', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span
                        draggable
                        onDragStart={(e) => onRowDragStart(e, row.id)}
                        style={{ fontSize: '16px', color: '#94a3b8', userSelect: 'none', cursor: 'grab', flexShrink: 0 }}
                        title="Drag to reorder row"
                      >⠿</span>
                      {editingClient?.rowId === row.id ? (
                        <div style={{ display: 'flex', gap: '6px', flex: 1 }}>
                          <input
                            type="text"
                            value={editClientValue}
                            onChange={(e) => setEditClientValue(e.target.value)}
                            onKeyPress={(e) => { if (e.key === 'Enter') saveEditClient(row.id); }}
                            autoFocus
                            style={{ flex: 1, padding: '4px 6px', borderRadius: '4px', border: '1px solid #800020', outline: 'none', fontSize: '13px' }}
                          />
                          <button
                            onClick={() => saveEditClient(row.id)}
                            style={{ background: '#10b981', color: 'white', border: 'none', padding: '2px 8px', borderRadius: '4px', cursor: 'pointer', fontSize: '11px' }}
                          >
                            Save
                          </button>
                          <button
                            onClick={cancelEditClient}
                            style={{ background: '#9ca3af', color: 'white', border: 'none', padding: '2px 8px', borderRadius: '4px', cursor: 'pointer', fontSize: '11px' }}
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flex: 1 }}>
                          <span>{row.client}</span>
                          {canEdit && (
                            <button
                              onClick={() => startEditClient(row.id, row.client)}
                              style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '12px', color: '#2563eb', padding: '2px 6px', borderRadius: '4px' }}
                              title="Edit client name"
                            >
                              ✏️
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  </td>

                  {/* Items column */}
                  <td
                    style={{ padding: '14px 16px', verticalAlign: 'top', borderBottom: '1px solid #e2e8f0', borderRight: '1px solid #e2e8f0', background: itemDragOver.rowId === row.id && itemDragOver.index === null ? '#e6f0ff' : columnColors.items.bg, transition: 'background 0.15s', wordBreak: 'break-word' }}
                    onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; }}
                    onDrop={(e) => onItemsColumnDrop(e, row.id)}
                    onDragEnter={(e) => { e.preventDefault(); setItemDragOver({ rowId: row.id, index: null }); }}
                    onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setItemDragOver({ rowId: null, index: null }); }}
                  >
                    {renderItemCell(row.id, row.items)}
                  </td>

                  {/* Carpenters column – mirrors items order */}
                  <td
                    style={{ padding: '14px 16px', verticalAlign: 'top', borderBottom: '1px solid #e2e8f0', borderRight: '1px solid #e2e8f0', background: columnColors.carpenters.bg, transition: 'background 0.15s', wordBreak: 'break-word' }}
                  >
                    {renderCarpenterCell(row.id, row.carpenters)}
                  </td>

                  {canEdit && (
                    <td style={{ padding: '14px 16px', textAlign: 'center', borderBottom: '1px solid #e2e8f0', background: 'white' }}>
                      <button onClick={() => handleDeleteClient(row.id, row.client)}
                        style={{ background: '#fef2f2', color: '#dc2626', border: '1px solid #fee2e2', padding: '6px 12px', borderRadius: '8px', cursor: 'pointer', fontSize: '11px', transition: 'all 0.2s ease' }}
                        onMouseEnter={e => { e.currentTarget.style.background = '#dc2626'; e.currentTarget.style.color = 'white'; }}
                        onMouseLeave={e => { e.currentTarget.style.background = '#fef2f2'; e.currentTarget.style.color = '#dc2626'; }}
                      >🗑️ Delete</button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Legend */}
      {clientsData.length > 0 && (
        <div style={{ marginTop: '20px', padding: '14px 20px', background: 'white', borderRadius: '12px', display: 'flex', gap: '20px', flexWrap: 'wrap', justifyContent: 'center', border: '1px solid #e2e8f0' }}>
          {Object.entries(columnColors).map(([key, val]) => (
            <div key={key} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <div style={{ width: '16px', height: '16px', background: val.bg, border: `1px solid ${val.border}`, borderRadius: '4px' }} />
              <span style={{ fontSize: '12px', color: '#475569' }}>{key === 'rowNumber' ? 'Row #' : key.replace(/^./, s => s.toUpperCase())}</span>
            </div>
          ))}
          <span style={{ fontSize: '12px', color: '#475569' }}>⠿ Drag grip to reorder rows | ⠿ Drag pills to move/reorder | ✏️ Edit client name | 📄 PDF</span>
        </div>
      )}
    </div>
  );
};

export default ClientView;